import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import Checkbox from '@sharedCR/common/Checkbox';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import _ from 'lodash';

import { getConditionalArray } from 'utils/data';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';
import { pagesPropTypes } from '../../MainContent.constants';

import { getCreditReportingTableConfig } from './CreditReporting.config';
import { keyFactorsItemStyles, wrapperStyles } from './CreditReporting.styles';

const isChecked = (middleFico, currentFico) =>
  !_.isNil(middleFico) && !_.isNil(currentFico) && middleFico === currentFico;

const CreditReporting = ({ isLoading, isSaving, className, entityData: applicationData }) => {
  const { creditReportData } = applicationData ?? {};
  const { creditRating } = creditReportData ?? {};
  const { equifaxFico, experianFico, transUnionFico, middleFico } = creditRating ?? {};

  const formattedKeyFactors = useMemo(() => {
    if (!creditRating) {
      return [];
    }

    const { creditRatingBureaus } = creditRating;

    const biggestKeyFactorsLength = _.max(
      creditRatingBureaus?.map(bureaus => bureaus.keyFactors?.length),
    );

    if (!biggestKeyFactorsLength) {
      return [
        {
          equifax: '-',
          experian: '-',
          transUnion: '-',
        },
      ];
    }

    return Array.from({ length: biggestKeyFactorsLength }, (__, i) =>
      creditRatingBureaus.reduce((acc, cur) => {
        const key = _.lowerFirst(cur.type);

        const curKeyFactor = cur.keyFactors[i];
        const { code, description } = curKeyFactor ?? {};

        acc[key] = curKeyFactor ? (
          <Typography key={`${code}-${description}`} sx={keyFactorsItemStyles} variant="regular14">
            <span>{code}</span> - {_.capitalize(description)}
          </Typography>
        ) : (
          '-'
        );

        return acc;
      }, {}),
    );
  }, [creditRating]);

  const formattedData = useMemo(
    () => [
      ...getConditionalArray(!!creditRating, () => [
        {
          bureaus: {
            value: 'Scores',
          },
          equifax: equifaxFico ?? '-',
          experian: experianFico ?? '-',
          transUnion: transUnionFico ?? '-',
        },
        {
          bureaus: {
            value: 'UD',
          },
          equifax: (
            <Checkbox
              disabled
              checked={isChecked(middleFico, equifaxFico)}
              data-testid={BorrowersTestIds.NBE_CREDIT_REPORTING_EQUIFAX_UD_CHECKBOX}
            />
          ),
          experian: (
            <Checkbox
              disabled
              checked={isChecked(middleFico, experianFico)}
              data-testid={BorrowersTestIds.NBE_CREDIT_REPORTING_EXPERIAN_UD_CHECKBOX}
            />
          ),
          transUnion: (
            <Checkbox
              disabled
              checked={isChecked(middleFico, transUnionFico)}
              data-testid={BorrowersTestIds.NBE_CREDIT_REPORTING_TRANSUNION_UD_CHECKBOX}
            />
          ),
        },
        {
          bureaus: {
            value: 'Key Factors',
            rowSpan: formattedKeyFactors.length,
            position: 1,
          },
          ...formattedKeyFactors[0],
        },
        ...formattedKeyFactors.slice(1),
      ]),
      ...getConditionalArray(!creditRating, [
        {
          bureaus: {
            position: 1,
            value: 'Credit has not been pulled for this NBE',
            isDescription: true,
            colSpan: 4,
          },
        },
      ]),
    ],
    [equifaxFico, experianFico, transUnionFico, middleFico, formattedKeyFactors, creditRating],
  );

  return (
    <Box className={className} sx={wrapperStyles}>
      <TableCDS
        isCustomDataDefinition
        withInternalBorders
        config={getCreditReportingTableConfig()}
        data={formattedData}
        isLoading={isLoading && !isSaving}
        withDivider={false}
      />
    </Box>
  );
};

CreditReporting.propTypes = pagesPropTypes;

export default memo(CreditReporting);
